import { useEffect, useRef, useState } from 'react';
import StatusBadge from '@/components/base/StatusBadge';
import { fetchOrderItems, updateOrder, type Order, type OrderItem } from '@/lib/data';
import { useRestaurant } from '@/lib/session';

interface OrderDetailDrawerProps {
  order: Order | null;
  open: boolean;
  onClose: () => void;
  onUpdated: (id: string, patch: Partial<Order>) => void;
}

function shortId(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

export default function OrderDetailDrawer({ order, open, onClose, onUpdated }: OrderDetailDrawerProps) {
  const restaurant = useRestaurant();
  const drawerRef = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<OrderItem[] | null>(null);
  const [itemsError, setItemsError] = useState('');
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const orderId = order?.id ?? null;
  useEffect(() => {
    setItems(null);
    setItemsError('');
    setActionError('');
    if (!open || !orderId) return;
    let cancelled = false;
    fetchOrderItems(orderId)
      .then((rows) => { if (!cancelled) setItems(rows); })
      .catch((e: unknown) => {
        if (!cancelled) setItemsError(e instanceof Error ? e.message : 'Could not load order items.');
      });
    return () => { cancelled = true; };
  }, [open, orderId]);

  if (!order) return null;

  const applyPatch = async (patch: Partial<Order>) => {
    setSaving(true);
    setActionError('');
    try {
      await updateOrder(restaurant.id, order.id, patch);
      onUpdated(order.id, patch);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not update order.');
    } finally {
      setSaving(false);
    }
  };

  const halfGst = restaurant.gstRate / 2;
  const isFinal = order.status === 'served' || order.status === 'cancelled';
  const timeline: { status: string; time: string }[] = [
    { status: 'Order received', time: formatDateTime(order.placedAt) },
  ];
  if (order.completedAt) {
    timeline.push({
      status: order.status === 'cancelled' ? 'Cancelled' : 'Served',
      time: formatDateTime(order.completedAt),
    });
  }

  return (
    <>
      {open && <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose}></div>}
      <div
        ref={drawerRef}
        className={`fixed top-0 right-0 h-full w-full max-w-lg bg-background-50 z-50 transform transition-transform duration-250 shadow-lg flex flex-col ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200 shrink-0">
          <div>
            <div className="flex items-center gap-3">
              <h3 className="text-base font-semibold text-foreground-800">{shortId(order.id)}</h3>
              <StatusBadge status={order.status} />
            </div>
            <p className="text-xs text-foreground-500 mt-1">
              {order.type} {order.tableId ? `· ${order.tableId}` : ''} · {order.customer}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-200 transition-colors cursor-pointer">
            <i className="ri-close-line text-lg text-foreground-600"></i>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Items */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Order Items</h4>
            {items === null && !itemsError && (
              <div className="py-6 text-center">
                <i className="ri-loader-4-line text-xl text-primary-500 animate-spin"></i>
                <p className="text-xs text-foreground-500 mt-2">Loading items…</p>
              </div>
            )}
            {itemsError && (
              <div className="py-4 text-center">
                <p className="text-xs text-accent-500">{itemsError}</p>
              </div>
            )}
            {items !== null && items.length === 0 && !itemsError && (
              <p className="text-xs text-foreground-500 py-2">No line items found for this order.</p>
            )}
            <div className="space-y-3">
              {(items ?? []).map((item) => (
                <div key={item.id} className="flex items-start justify-between p-3 rounded-lg bg-background-100/70 border border-background-200/70">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${item.price > 200 ? 'bg-red-500' : 'bg-green-500'}`}></span>
                      <span className="text-sm font-medium text-foreground-800">{item.name}</span>
                    </div>
                    {item.modifiers && item.modifiers.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5 ml-4">
                        {item.modifiers.map((m) => (
                          <span key={m} className="text-xs px-2 py-0.5 rounded-full bg-background-200/70 text-foreground-600">{m}</span>
                        ))}
                      </div>
                    )}
                    {item.specialInstructions && (
                      <p className="text-xs text-foreground-500 mt-1.5 ml-4 italic">"{item.specialInstructions}"</p>
                    )}
                  </div>
                  <div className="text-right ml-4 shrink-0">
                    <span className="text-sm font-medium text-foreground-800">x{item.qty}</span>
                    <p className="text-xs text-foreground-500">₹{(item.price * item.qty).toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bill Summary */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Bill Summary</h4>
            <div className="p-4 rounded-lg bg-background-100/70 border border-background-200/70 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-foreground-600">Subtotal</span>
                <span className="text-foreground-800">₹{order.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-foreground-600">CGST ({halfGst}%)</span>
                <span className="text-foreground-800">₹{order.cgst.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-foreground-600">SGST ({halfGst}%)</span>
                <span className="text-foreground-800">₹{order.sgst.toFixed(2)}</span>
              </div>
              <div className="border-t border-background-200 pt-2 flex justify-between">
                <span className="text-sm font-semibold text-foreground-900">Total</span>
                <span className="text-sm font-semibold text-foreground-900">₹{order.total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Payment */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Payment</h4>
            <div className="flex items-center gap-6 p-4 rounded-lg bg-background-100/70 border border-background-200/70">
              <div>
                <p className="text-xs text-foreground-500">Method</p>
                <p className="text-sm font-medium text-foreground-800 mt-0.5">{order.paymentMode}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-500">Status</p>
                <StatusBadge status={order.paymentStatus} size="sm" />
              </div>
              {order.paymentStatus === 'pending' && (
                <button
                  onClick={() => applyPatch({ paymentStatus: 'paid' })}
                  disabled={saving}
                  className="ml-auto px-3 py-1.5 rounded-md text-xs font-medium bg-secondary-500 text-background-50 hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  Mark Paid
                </button>
              )}
            </div>
          </div>

          {/* KOT Timeline */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">KOT Timeline</h4>
            <div className="relative pl-5 space-y-3">
              {timeline.map((event, i) => (
                <div key={i} className="relative">
                  <div className={`absolute -left-3.5 top-1.5 w-2 h-2 rounded-full border-2 ${i === timeline.length - 1 ? 'bg-accent-500 border-accent-500' : 'bg-background-50 border-foreground-300'}`}></div>
                  {i < timeline.length - 1 && (
                    <div className="absolute -left-2.5 top-4 w-px h-full bg-foreground-200"></div>
                  )}
                  <div>
                    <p className="text-sm text-foreground-800">{event.status}</p>
                    <p className="text-xs text-foreground-500">{event.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {actionError && (
            <p className="text-xs text-accent-500">{actionError}</p>
          )}
        </div>

        {/* Footer actions */}
        <div className="border-t border-background-200 px-5 py-3 flex items-center gap-3 shrink-0">
          {order.status === 'received' && (
            <button
              onClick={() => applyPatch({ status: 'preparing' })}
              disabled={saving}
              className="flex-1 bg-primary-500 text-background-50 px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              Accept & Start Prepping
            </button>
          )}
          {order.status === 'preparing' && (
            <button
              onClick={() => applyPatch({ status: 'ready' })}
              disabled={saving}
              className="flex-1 bg-accent-500 text-background-50 px-4 py-2 rounded-md text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              Mark as Ready
            </button>
          )}
          {order.status === 'ready' && (
            <button
              onClick={() => applyPatch({ status: 'served', completedAt: new Date().toISOString() })}
              disabled={saving}
              className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              Mark as Served
            </button>
          )}
          {!isFinal && (
            <button
              onClick={() => applyPatch({ status: 'cancelled', completedAt: new Date().toISOString() })}
              disabled={saving}
              className="px-3 py-2 rounded-md text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              Cancel Order
            </button>
          )}
          <button className="px-3 py-2 rounded-md text-sm text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
            Print KOT
          </button>
        </div>
      </div>
    </>
  );
}

